import type { CoachJourneyInput, CoachTransitionInput } from '../schema';

const REOPEN = {
  title: 'Pick up where you left off.',
  body: 'Open your Content Project to keep going. Your script, take, and edits are still there.',
};

/** Copilot can finish the lesson for them: posting or scheduling from Copilot ends it too. */
const COPILOT_POSTED: CoachTransitionInput = {
  event: 'post.sent',
  match: ['boardId', 'projectNodeId'],
  to: 'end',
};

/**
 * Hands-on practice that follows the Script. Shoot. Post. lesson: from an idea to a posted
 * video, on the user's own board. Copilot leads, but any way of getting a script onto the
 * board moves it forward: a board chat's script card, the toolbar, Content Studio, Trend Radar.
 */
export const scriptShootPostJourney = {
  id: 'script-shoot-post',
  version: 3,
  name: 'Script. Shoot. Post.',
  description: 'Take an idea through filming, editing, and posting on your own board.',
  gate: { flag: 'scriptShootPost' },
  steps: [
    {
      id: 'choose-board',
      display: 'hidden',
      title: 'Open a board.',
      body: 'Open any board you can edit to start practicing.',
      on: [{ event: 'board.entered', match: ['boardId'], bind: ['boardId'], to: 'ask' }],
    },
    {
      id: 'ask',
      chapter: 1,
      title: 'Ask for your script.',
      body: 'Tell Copilot your video idea and who it’s for, and ask for a 30-second reel script. It lands on your board as a script card.',
      show: [
        { anchor: 'copilot.input' },
        {
          anchor: 'toolbar.copilot',
          title: 'Open Copilot.',
          body: 'Click Copilot and tell it your video idea. It writes the script straight onto your board.',
        },
      ],
      on: [
        {
          event: 'project.created',
          match: ['boardId'],
          bind: ['projectNodeId'],
          fresh: true,
          to: 'record',
        },
        { event: 'copilot.sent', match: ['boardId'], to: 'writing' },
        { event: 'chat.sent', match: ['boardId'], bind: ['chatNodeId'], to: 'await-script' },
      ],
    },
    {
      id: 'writing',
      chapter: 1,
      display: 'toast',
      title: 'Copilot is writing.',
      body: 'Copilot is writing your script ✨ It lands on your board as a script card.',
      resumeAt: 'ask',
      on: [
        {
          event: 'project.created',
          match: ['boardId'],
          bind: ['projectNodeId'],
          fresh: true,
          to: 'record',
        },
        { event: 'copilot.replied', match: ['boardId'], to: 'reply' },
      ],
    },
    {
      id: 'reply',
      chapter: 1,
      title: 'Tell Copilot to write it.',
      body: 'If it asked you something, answer it. Or just say “write it as a script” and it lands on your board.',
      show: [
        { anchor: 'copilot.input' },
        {
          anchor: 'toolbar.copilot',
          body: 'Open Copilot and ask it to write your idea as a script.',
        },
      ],
      on: [
        {
          event: 'project.created',
          match: ['boardId'],
          bind: ['projectNodeId'],
          fresh: true,
          to: 'record',
        },
        { event: 'copilot.sent', match: ['boardId'], to: 'writing' },
        { event: 'chat.sent', match: ['boardId'], bind: ['chatNodeId'], to: 'await-script' },
      ],
    },
    {
      id: 'await-script',
      chapter: 1,
      title: 'Your script is on its way ✨',
      body: 'When Poppy gives you a script card, I’ll show you the quickest way to film it. If you get a regular reply instead, ask her to create it as a Content Project.',
      show: [{ anchor: 'chat.input' }],
      focus: { node: 'chatNodeId' },
      action: { label: 'Try another message', to: 'ask', unbind: ['chatNodeId'] },
      resumeAt: 'ask',
      on: [
        {
          event: 'chat.script-ready',
          match: ['boardId', 'chatNodeId'],
          bind: ['toolCallId'],
          fresh: true,
          to: 'film',
        },
        {
          event: 'project.created',
          match: ['boardId'],
          bind: ['projectNodeId'],
          fresh: true,
          to: 'record',
        },
        { event: 'copilot.sent', match: ['boardId'], to: 'writing' },
      ],
    },
    {
      id: 'film',
      chapter: 1,
      title: 'Here’s your script.',
      body: 'Read the hook and make sure it sounds like you. Click Add to board to save it. Then open the post to record, upload, or edit.',
      show: [{ anchor: 'chat.script-card.add-to-board' }],
      focus: { node: 'chatNodeId' },
      on: [
        {
          event: 'project.created',
          match: ['boardId'],
          bind: ['projectNodeId'],
          fresh: true,
          to: 'record',
        },
        // A script card added before opens its existing project instead of making a new one.
        { event: 'studio.opened', match: ['boardId'], bind: ['projectNodeId'], to: 'record' },
      ],
    },
    {
      id: 'record',
      chapter: 2,
      title: 'Choose your camera.',
      body: 'Your post keeps the script and post text together. Click Record, then use your computer or scan the QR code for your phone. Use Upload video for an existing video. Save a take you like to keep going.',
      show: [
        {
          anchor: 'studio.record',
          when: { studio: 'open' },
          body: 'Click Record, then choose your computer or phone. Upload video works too if you already have a video.',
          spotlight: false,
        },
        { anchor: 'studio.recording-entry', when: { studio: 'open' } },
        {
          anchor: 'project.open-studio',
          when: { studio: 'closed' },
          title: 'Your script is on your board.',
          body: 'Click Open to see your script on the left and post text on the right. Use Record or Upload video from there.',
        },
      ],
      focus: { node: 'projectNodeId' },
      on: [
        { event: 'project.take-saved', match: ['boardId', 'projectNodeId'], to: 'edit' },
        { event: 'studio.camera-chosen', match: ['boardId', 'projectNodeId'], to: 'wait-take' },
      ],
    },
    {
      id: 'wait-take',
      chapter: 2,
      display: 'toast',
      title: 'Film your take.',
      body: 'Film your take. I’ll be here when it saves.',
      show: [
        {
          when: { studio: 'closed' },
          body: 'Open your project to finish filming. I’ll pick up when the take saves.',
        },
      ],
      focus: { node: 'projectNodeId' },
      on: [{ event: 'project.take-saved', match: ['boardId', 'projectNodeId'], to: 'edit' }],
    },
    {
      id: 'edit',
      chapter: 3,
      title: 'Make the cut yours.',
      body: 'Play your saved take. Drag the timeline edges if you want a tighter start or finish. Your original video stays safe.',
      show: [
        { anchor: 'studio.timeline', when: { studio: 'open' }, spotlight: false },
        {
          anchor: 'studio.edit-media',
          when: { studio: 'open' },
          body: 'Click Edit video to open the timeline. Your post text stays saved.',
          spotlight: false,
        },
        { anchor: 'project.open-studio', when: { studio: 'closed' }, ...REOPEN },
      ],
      focus: { node: 'projectNodeId' },
      action: { label: 'Show captions', to: 'captions' },
      on: [
        { event: 'post.opened', match: ['boardId', 'projectNodeId'], to: 'publish' },
        COPILOT_POSTED,
      ],
    },
    {
      id: 'captions',
      chapter: 3,
      title: 'Add captions if you want them.',
      body: 'Open Captions to choose a style and fix any words. Poppy times the words after your take saves.',
      show: [
        { anchor: 'studio.captions', when: { studio: 'open' }, spotlight: false },
        {
          anchor: 'studio.edit-media',
          when: { studio: 'open' },
          body: 'Click Edit video, then open Captions to style the words on your video.',
          spotlight: false,
        },
        { anchor: 'project.open-studio', when: { studio: 'closed' }, ...REOPEN },
      ],
      focus: { node: 'projectNodeId' },
      action: { label: 'Show posting', to: 'post' },
      on: [
        { event: 'post.opened', match: ['boardId', 'projectNodeId'], to: 'publish' },
        COPILOT_POSTED,
      ],
    },
    {
      id: 'post',
      chapter: 4,
      title: 'Ready to post.',
      body: 'Back on your post, finish the post text on the right. Posting is below: choose an account and Now or a future date. Plan saves a day without publishing. Your latest video edit is prepared automatically.',
      do: {
        action: 'project.open',
        params: { project: 'projectNodeId', view: 'post', delivery: 'post' },
        then: 'publish',
      },
      show: [
        { anchor: 'studio.delivery', when: { studio: 'open' }, spotlight: false },
        { anchor: 'studio.back-to-post', when: { studio: 'open' }, spotlight: false },
        { anchor: 'project.open-studio', when: { studio: 'closed' }, ...REOPEN },
      ],
      focus: { node: 'projectNodeId' },
      on: [
        { event: 'post.opened', match: ['boardId', 'projectNodeId'], to: 'publish' },
        COPILOT_POSTED,
      ],
    },
    {
      id: 'post-return',
      chapter: 4,
      title: 'Your post is saved.',
      body: 'You can keep editing or come back later. Open your post when you’re ready to finish the post text and choose Post or Plan.',
      show: [{ anchor: 'project.open-studio', when: { studio: 'closed' }, ...REOPEN }],
      action: { label: 'Open my post', to: 'post' },
      on: [{ event: 'post.opened', match: ['boardId', 'projectNodeId'], to: 'publish' }],
    },
    {
      id: 'publish',
      chapter: 4,
      display: 'inline',
      slot: 'post-dialog',
      title: 'One last check.',
      body: 'Your post text is above. Choose an account here, then keep Now or select a future time. Plan saves a day without publishing or requiring an account. The final button confirms your choice.',
      on: [
        { event: 'post.closed', match: ['boardId', 'projectNodeId'], to: 'post-return' },
        COPILOT_POSTED,
        { event: 'project.planned', match: ['boardId', 'projectNodeId'], to: 'end' },
        { event: 'post.accepted', match: ['boardId', 'projectNodeId'], to: 'end' },
      ],
    },
  ],
} satisfies CoachJourneyInput;
